import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useAuth } from "hooks/useAuth";
import PivotGridDataSource from "devextreme/ui/pivot_grid/data_source";
import { osirisApi } from "services/api/osiris";
import { SelectBox } from "devextreme-react/select-box";
import PyramidCard from "components/common/pyramid/PyramidCard";
import PyramidPivotChart from "components/common/pyramid/PyramidPivotChart";
import PyramidPivotGrid from "components/common/pyramid/PyramidPivotGrid";
import { Row, Col } from "react-bootstrap";
import { getClientPalette, defaultPalette } from "config/clientPalettes";
import { useGroupedItems } from "hooks/useGroupedItems";

const ControlAnalysis = () => {
  const [computations, setComputations] = useState([]);
  const [selectedComputation, setSelectedComputation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [pivotData, setPivotData] = useState([]);
  const [pivotHeaders, setPivotHeaders] = useState([]);
  const [dataSource, setDataSource] = useState(null);
  const [currentPalette, setCurrentPalette] = useState(defaultPalette);
  const { accessToken, account, companyList, userList } = useAuth();
  const api = osirisApi();
  const chartRef = useRef(null);
  const pivotGridRef = useRef(null);

  // Grouping computations by user
  const groupedComputations = useGroupedItems(
    computations,
    userList,
    companyList
  );

  // Effect to update palette when account changes
  useEffect(() => {
    if (account && companyList && companyList.length > 0) {
      const newPalette = getClientPalette(companyList[0].id || "default");
      setCurrentPalette(newPalette);
    }
  }, [account, companyList]);

  // Fetching pivot headers
  useEffect(() => {
    const fetchPivotHeaders = async () => {
      if (!accessToken) return;
      try {
        const data = await api.getPivotHeaderControl();
        const headers = Array.isArray(data) ? data : data.result;
        if (headers) {
          setPivotHeaders(headers);
        }
      } catch (error) {
        console.error("Error fetching pivot headers:", error);
      }
    };

    fetchPivotHeaders();
  }, [accessToken]);

  // Fetching available computations
  useEffect(() => {
    const fetchComputations = async () => {
      if (!accessToken) return;
      try {
        setIsLoading(true);
        const data = await api.getComputations({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        });

        if (data) {
          const computations = Array.isArray(data) ? data : data.result || [];
          const sortedComputations = [...computations].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setComputations(sortedComputations);
        }
      } catch (error) {
        console.error("Error fetching computations:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComputations();
  }, [accessToken]);

  const filterResults = useCallback((results, headers) => {
    if (!headers?.length) return results;

    const validFields = headers.map((header) => header.fieldName);
    return results.map((item) => {
      const filteredItem = {};
      validFields.forEach((field) => {
        if (item.hasOwnProperty(field)) {
          filteredItem[field] = item[field];
        }
      });
      return filteredItem;
    });
  }, []);

  // Fetching selected computation results
  useEffect(() => {
    const fetchResults = async () => {
      if (!selectedComputation || !accessToken) return;

      try {
        setIsDataLoading(true);
        const response = await api.getSelectedComputationControl(
          selectedComputation
        );
        const data =
          typeof response === "string" ? JSON.parse(response) : response;

        const results = Array.isArray(data) ? data : data.result || [];
        setPivotData(results);
      } catch (error) {
        console.error("Error fetching results:", error);
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchResults();
  }, [selectedComputation, accessToken]);

  const filteredPivotData = useMemo(() => {
    return filterResults(pivotData, pivotHeaders);
  }, [pivotData, pivotHeaders, filterResults]);

  useEffect(() => {
    if (filteredPivotData.length > 0 && pivotHeaders?.length) {
      const fields = getFields();

      const newDataSource = new PivotGridDataSource({
        fields: fields,
        store: filteredPivotData,
      });

      setDataSource(newDataSource);
    }
  }, [filteredPivotData, pivotHeaders]);

  const getFields = () => {
    if (!pivotHeaders || !pivotHeaders.length) {
      return [];
    }

    return pivotHeaders.map((header) => {
      const field = {
        caption: header.caption,
        dataField: header.fieldName,
        displayName: header.caption,
      };

      switch (header.caption) {
        case "Geo Area":
          field.area = "row";
          field.areaIndex = 0;
          break;
        case "Sector":
          field.area = "column";
          field.areaIndex = 0;
          break;
        case "ECL Gap Abs":
          field.dataType = "number";
          field.summaryType = "sum";
          field.format = header.valueFormat || "currency";
          field.area = "data";
          field.areaIndex = 0;
          break;
        default:
          field.area = undefined;
          break;
      }

      if (header.valueFormat) {
        field.format = header.valueFormat;
        if (header.valueFormat === ",##0€") {
          field.summaryType = "sum";
        }
      }

      return field;
    });
  };

  const handlePivotGridContentReady = (e) => {
    e.component.bindChart(document.getElementById("pivotgrid-chart"), {
      dataFieldsDisplayMode: "splitPanes",
      alternateDataFields: false,
    });
  };

  return (
    <div className="p-4 space-y-4">
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <PyramidCard className="h-100 p-2">
            <div className="d-flex flex-column h-100 py-2">
              <div className="fw-medium fs-7 text-nowrap mt-n3 pb-2">Choose your computation</div>
              <SelectBox
                dataSource={groupedComputations}
                displayExpr="description"
                valueExpr="id"
                grouped={true}
                value={selectedComputation}
                onValueChanged={(e) => setSelectedComputation(e.value)}
                disabled={isLoading}
                searchEnabled={true}
                placeholder="Choose"
                className="w-100"
              />
            </div>
          </PyramidCard>
        </Col>
      </Row>

      <div className="position-relative">
        {isDataLoading && (
          <div className="loading-overlay">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        <Row className="g-3">
          <Col xs={12}>
            <PyramidPivotChart
              chartRef={chartRef}
              palette={currentPalette}
              title="Control - Analysis"
            />
            <div className="mt-4">
              <PyramidPivotGrid
                pivotGridRef={pivotGridRef}
                dataSource={dataSource}
                palette={currentPalette}
                onContentReady={handlePivotGridContentReady}
                title=" "
              />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ControlAnalysis;
